 import React, { useState, useEffect, useRef, useContext, useMemo } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import SearchableSelect from '../../../../components/Common/SearchableSelect';
import { Chip, Typography } from '@mui/material';
import Modal from '../../../../components/Modals/Modal';
import * as mammoth from 'mammoth/mammoth.browser';

import { getAllAyudas, getAyudaDetalle, saveAyuda, resetAyudasIsaAdmin } from '../../../../api/ayudas/ayudasApi';
import { apiGetConsecutivos } from '../../../../api/consecutivoApi';
import { apiGetGrupos } from '../../../../api/gruposApi';
import { apiGetSubGrupos } from '../../../../api/subGruposApi';
import { extractAyudasIsaRowsFromHtml } from '../../../../utils/docxAyudasIsaImport';
import { AuthContext } from '../../../../context/AuthContext';

import {
  Page, Left, Right, HeaderRow,
  RoleListContainer, RoleItem, SearchInput,
} from "../styles/component.style";
import { BtnGeneral } from "../../../../styles/Buttons";

// ================= STYLES =================
const inp = { 
  flex: 1, padding: "5px 7px", borderRadius: "4px", border: "1px solid #cbd5e1", 
  fontSize: "12px", boxSizing: "border-box", outline: "none", marginBottom: 0, width: "100%" 
};

const selectSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    borderRadius: "4px",
    fontSize: "12px",
    fontFamily: "var(--font-body)",
    backgroundColor: "#ffffff",
    "& fieldset": { borderColor: "#cbd5e1" },
    "&:hover fieldset": { borderColor: "#94a3b8" },
    "&.Mui-focused fieldset": { borderColor: "#007bba" },
  },
  "& .MuiSelect-select": {
    padding: "5px 8px !important",
    fontSize: "12px",
    fontFamily: "var(--font-body)",
  },
};

const EditorContainer = styled.div`
  margin-bottom: 20px;
  position: relative;
  
  .ql-container {
    border-radius: 4px;
    font-family: inherit;
    font-size: 13px;
    transition: all 0.2s ease-in-out;
  }
  
  .ql-editor {
    min-height: 80px;
  }

  /* Cuando el editor tiene foco, resalta el contenedor */
  &:focus-within .ql-container.ql-snow {
    border: 2px solid #065f46 !important;
    box-shadow: 0 0 8px rgba(6,95,70,0.3);
  }

  /* Ocultar todas las barras por defecto */
  .ql-toolbar {
    display: none;
    background: #f8fafc;
    border-radius: 8px 8px 0 0;
    border: 1px solid #ccc;
    border-bottom: none;
  }

  /* Mostrar SOLO la barra del editor activo simulando que es única */
  &:focus-within .ql-toolbar {
    display: block;
    position: sticky;
    top: -10px;
    z-index: 1000;
  }
`;

// ================= COMPONENTS =================
function Accordion({ title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ marginBottom: "15px", border: "1px solid #e2e8f0", borderRadius: "6px" }}>
      <button onClick={() => setOpen(!open)} style={{
        width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "8px 12px", background: open ? "#f1f5f9" : "#f8fafc",
        border: "none", borderRadius: open ? "6px 6px 0 0" : "6px",
        cursor: "pointer", fontSize: "12px", fontWeight: "600", color: "#1e40af",
      }}>
        <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>{title}</span>
        <span style={{ fontSize: "10px", color: "#94a3b8" }}>{open ? "▲" : "▼"}</span>
      </button>
      {open && <div style={{ padding: "12px 15px", borderTop: "1px solid #e2e8f0" }}>{children}</div>}
    </div>
  );
}

function Field({ lbl, required, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "5px", width: "100%" }}>
      <label style={{ fontSize: "12px", color: "#64748b", fontWeight: "600" }}>
        {lbl}{required && <span style={{ color: "#ef4444", marginLeft: "2px" }}>*</span>}
      </label>
      {children}
    </div>
  );
}

const modules = {
  toolbar: [
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'color': [] }, { 'background': [] }],
    ['clean']
  ]
};

const initialForm = {
  id: null,
  actividad_consecutivo: '',
  grupo: '',
  subgrupo: '',
  titulo: '',
  fecha_revision: '',
  estado: 1,
  tags: [],
  desc_richtext: '',
  cond_richtext: '',
  puntos_richtext: '',
  info_eval_richtext: '',
  info_interv_richtext: '',
  info_consej_richtext: '',
  info_alarmas_richtext: '',
  info_registro_richtext: '',
  info_contra_richtext: '',
  freq_richtext: '',
  excep_richtext: '',
  source_rich: '',
};

export default function AyudasISA() {
  const { user } = useContext(AuthContext);
  const getTodayISO = () => {
    const d = new Date();
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 10);
  };

  const wordInputRef = useRef(null);

  const [formData, setFormData] = useState({ ...initialForm, fecha_revision: getTodayISO() });
  const [loading, setLoading] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [search, setSearch] = useState('');
  const [ayudas, setAyudas] = useState([]);
  const [importingWord, setImportingWord] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importWordFileName, setImportWordFileName] = useState('');
  const [importRows, setImportRows] = useState([]);
  const [importFilterText, setImportFilterText] = useState('');
  const [importFilterStatus, setImportFilterStatus] = useState('all');
  const [currentImportRowKey, setCurrentImportRowKey] = useState(null);
  const [resetConfirmText, setResetConfirmText] = useState('');
  const [resetToken, setResetToken] = useState('');
  
  // Data for dependency dropdowns
  const [consecutivos, setConsecutivos] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [subgrupos, setSubgrupos] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const results = await Promise.allSettled([
          apiGetConsecutivos(),
          apiGetGrupos(""),
          getAllAyudas()
        ]);
        if(results[0].status === 'fulfilled' && results[0].value?.data) setConsecutivos(results[0].value.data);
        if(results[1].status === 'fulfilled' && results[1].value?.data) setGrupos(results[1].value.data);
        if(results[2].status === 'fulfilled' && results[2].value?.data) setAyudas(results[2].value.data);
      } catch (err) {
        console.error("Error global de fetchData, Promise.allSettled no atrapó:", err);
      }
    };
    fetchData();
  }, []);

  // Update subgroup list whenever selected group changes
  useEffect(() => {
    let unmounted = false;
    const fetchSubgrupos = async () => {
      if(formData.grupo) {
        try {
          const res = await apiGetSubGrupos("", formData.grupo);
          if(!unmounted && res && res.data) setSubgrupos(res.data);
        } catch(e) {
            if(!unmounted) setSubgrupos([]);
        }
      } else {
        setSubgrupos([]);
      }
    };
    fetchSubgrupos();
    return () => { unmounted = true; };
  }, [formData.grupo]);

  const refreshAyudas = async () => {
    try {
      const res = await getAllAyudas();
      if(res && res.data) setAyudas(res.data);
    } catch(e) { console.error(e); }
  };

  const handleSelectAyuda = async (ayudaRaw) => {
    setLoading(true);
    try {
      const response = await getAyudaDetalle(ayudaRaw.actividad_consecutivo, ayudaRaw.grupo, ayudaRaw.subgrupo);
      const payload = response?.data; // Desempaquetar el envoltorio JSON del backend
      
      if (payload && payload.id) {
        setFormData({ ...initialForm, ...payload, tags: payload.tags || [] });
      } else {
        // Fallback or empty load
        setFormData({ ...initialForm, id: null, actividad_consecutivo: ayudaRaw.actividad_consecutivo, grupo: ayudaRaw.grupo, subgrupo: ayudaRaw.subgrupo });
      }
    } catch (e) {
      toast.error('Error al consultar ayuda.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNew = () => {
    setFormData({ ...initialForm, fecha_revision: getTodayISO() });
  };

  const handleSave = async () => {
    const today = getTodayISO();
    const payload = { ...formData, fecha_revision: today };

    setFormData((prev) => ({ ...prev, fecha_revision: today }));

    if (!payload.actividad_consecutivo || !payload.grupo || !payload.subgrupo) {
      toast.error('Faltan identificadores clave requeridos (Actividad, Grupo, Subgrupo).');
      return;
    }
    setLoading(true);
    try {
      await saveAyuda(payload);
      toast.success('Ayuda guardada correctamente.');
      await refreshAyudas();
      // Refetch detail to get assigned ID if new
      handleSelectAyuda(payload);
    } catch (e) {
      toast.error('Error al guardar la ayuda.');
    } finally {
      setLoading(false);
    }
  };

  const addTag = () => {
    if (tagInput.trim() !== '') {
      setFormData(prev => ({ ...prev, tags: [...prev.tags, tagInput.trim()] }));
      setTagInput('');
    }
  };

  const removeTag = (index) => {
    setFormData(prev => {
      const newTags = [...prev.tags];
      newTags.splice(index, 1);
      return { ...prev, tags: newTags };
    });
  };

  const consecutivosMap = useMemo(() => {
    const map = new Map();
    (consecutivos || []).forEach((c) => {
      if (c?.Consecutivo != null) {
        map.set(String(c.Consecutivo), String(c.Api || ''));
      }
    });
    return map;
  }, [consecutivos]);

  const gruposMap = useMemo(() => {
    const map = new Map();
    (grupos || []).forEach((g) => {
      if (g?.id != null) {
        map.set(String(g.id), String(g.descripcion || ''));
      }
    });
    return map;
  }, [grupos]);

  // Safe checks for the list render with text search across consecutivo, titulo, nombre de actividad, grupo y subgrupo
  const filteredAyudas = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return ayudas || [];

    const terms = q.split(/\s+/).filter(Boolean);

    return (ayudas || []).filter((a) => {
      const actNum = String(a.actividad_consecutivo || '').toLowerCase();
      const titulo = String(a.titulo || '').toLowerCase();
      const grp = String(a.grupo || '').toLowerCase();
      const subgrp = String(a.subgrupo || '').toLowerCase();
      const actApi = String(consecutivosMap.get(String(a.actividad_consecutivo)) || '').toLowerCase();
      const grpDesc = String(gruposMap.get(String(a.grupo)) || '').toLowerCase();

      const combined = `${actNum} ${actApi} ${titulo} ${grp} ${grpDesc} ${subgrp}`;

      return terms.every((term) => combined.includes(term));
    });
  }, [ayudas, search, consecutivosMap, gruposMap]);

  const consecutivoOptions = (consecutivos || []).map((c) => ({
    value: c.Consecutivo,
    label: `${c.Consecutivo} - ${c.Api}`,
  }));

  const grupoOptions = (grupos || []).map((g) => ({
    value: g.id,
    label: `${g.id} - ${g.descripcion}`,
  }));

  const subgrupoOptions = (subgrupos || []).map((s) => ({
    value: s.id,
    label: `${s.id} - ${s.descripcion}`,
  }));

  const handlePickWord = () => {
    wordInputRef.current?.click?.();
  };

  const buildRowKey = React.useCallback((row) => {
    const a = String(row?.actividad_consecutivo || '').trim();
    const g = String(row?.grupo || '').trim();
    const s = String(row?.subgrupo || '').trim();
    if (!a || !g || !s) return null;
    return `${a}__${g}__${s}`;
  }, []);

  const savedKeys = useMemo(() => {
    const set = new Set();
    (ayudas || []).forEach((a) => {
      const k = `${String(a.actividad_consecutivo || '').trim()}__${String(a.grupo || '').trim()}__${String(a.subgrupo || '').trim()}`;
      set.add(k);
    });
    return set;
  }, [ayudas]);

  const duplicateKeysInWord = useMemo(() => {
    const counts = new Map();
    (importRows || []).forEach((r) => {
      const k = buildRowKey(r);
      if (!k) return;
      counts.set(k, (counts.get(k) || 0) + 1);
    });
    const dup = new Set();
    counts.forEach((v, k) => {
      if (v > 1) dup.add(k);
    });
    return dup;
  }, [buildRowKey, importRows]);

  const importStats = useMemo(() => {
    const total = (importRows || []).length;
    let saved = 0;
    let duplicate = 0;
    let pending = 0;
    (importRows || []).forEach((r) => {
      const k = buildRowKey(r);
      if (!k) return;
      const isDup = duplicateKeysInWord.has(k);
      const isSaved = savedKeys.has(k);
      if (isDup) duplicate += 1;
      if (isSaved) saved += 1;
      if (!isDup && !isSaved) pending += 1;
    });
    return { total, saved, pending, duplicate };
  }, [buildRowKey, duplicateKeysInWord, importRows, savedKeys]);

  const filteredImportRows = useMemo(() => {
    const q = importFilterText.trim().toLowerCase();
    return (importRows || []).filter((r) => {
      const k = buildRowKey(r);
      const isDup = k ? duplicateKeysInWord.has(k) : false;
      const isSaved = k ? savedKeys.has(k) : false;

      if (importFilterStatus === 'saved' && !isSaved) return false;
      if (importFilterStatus === 'pending' && (isSaved || isDup)) return false;
      if (importFilterStatus === 'duplicate' && !isDup) return false;

      if (!q) return true;
      const hay = `${r.actividad_consecutivo || ''} ${r.grupo || ''} ${r.subgrupo || ''} ${r.titulo || ''} ${r.source_rich || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [buildRowKey, duplicateKeysInWord, importFilterStatus, importFilterText, importRows, savedKeys]);

  const handleWordFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImportWordFileName(file.name);
    setImportingWord(true);
    try {
      const arrayBuffer = await file.arrayBuffer();
      const convertFn = mammoth?.convertToHtml || mammoth?.default?.convertToHtml;
      if (typeof convertFn !== 'function') {
        throw new Error('mammoth.convertToHtml no disponible');
      }
      const result = await convertFn({ arrayBuffer });
      const extracted = extractAyudasIsaRowsFromHtml(result?.value || '');

      await refreshAyudas();
      setImportRows(extracted.rows || []);
      setImportFilterText('');
      setImportFilterStatus('all');

      if ((extracted.rows || []).length === 0) {
        toast.error('No se detectaron filas para importar desde el Word.');
      } else {
        toast.success(`Word cargado: ${(extracted.rows || []).length} filas detectadas. Ahora seleccione una fila y use "Cargar".`);
      }
    } catch (err) {
      const msg = (err && err.message) ? String(err.message) : '';
      toast.error(`Error leyendo el Word. ${msg ? `Detalle: ${msg}` : 'Verifique que sea un .docx válido.'}`);
    } finally {
      setImportingWord(false);
      e.target.value = '';
    }
  };

  const handleLoadImportRow = async (row) => {
    if (!row) return;

    const actividad_consecutivo = String(row.actividad_consecutivo || '').trim();
    const grupo = String(row.grupo || '').trim();
    const subgrupo = String(row.subgrupo || '').trim();
    const rowKey = buildRowKey(row);

    if (rowKey && duplicateKeysInWord.has(rowKey)) {
      toast.error('Esta fila está duplicada en el Word (misma llave Consecutivo/Grupo/Subgrupo).');
      return;
    }

    if (!actividad_consecutivo || !grupo || !subgrupo) {
      toast.error('La fila no tiene identificadores completos (Consecutivo, Grupo, Subgrupo).');
      return;
    }

    const existing = (ayudas || []).find(
      (a) =>
        String(a.actividad_consecutivo) === actividad_consecutivo &&
        String(a.grupo) === grupo &&
        String(a.subgrupo) === subgrupo
    );

    if (!existing) {
      setFormData({
        ...initialForm,
        ...row,
        actividad_consecutivo,
        grupo,
        subgrupo,
        fecha_revision: getTodayISO(),
        id: null,
      });
      setCurrentImportRowKey(rowKey);
      setIsImportModalOpen(false);
      toast.info('Fila cargada como nuevo registro.');
      return;
    }

    setLoading(true);
    try {
      const response = await getAyudaDetalle(actividad_consecutivo, grupo, subgrupo);
      const payload = response?.data;

      const base = payload && payload.id
        ? { ...initialForm, ...payload, tags: payload.tags || [] }
        : { ...initialForm };

      setFormData({
        ...base,
        ...row,
        actividad_consecutivo,
        grupo,
        subgrupo,
        fecha_revision: getTodayISO(),
        id: payload?.id || existing.id,
      });

      setCurrentImportRowKey(rowKey);
      setIsImportModalOpen(false);
      toast.info('Fila cargada para actualizar un registro existente.');
    } catch (err) {
      toast.error('Error consultando la ayuda existente para actualización.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetAyudasIsa = async () => {
    if (!user?.es_supersu) return;
    if (resetConfirmText !== 'RESET_AYUDAS_ISA') {
      toast.error('Debe escribir RESET_AYUDAS_ISA para confirmar.');
      return;
    }
    if (!String(resetToken || '').trim()) {
      toast.error('Debe ingresar el token de reseteo.');
      return;
    }

    setLoading(true);
    try {
      const res = await resetAyudasIsaAdmin({
        confirm: resetConfirmText,
        token: String(resetToken || '').trim(),
      });
      toast.success(res?.message || 'Tablas ISA reiniciadas.');
      setResetConfirmText('');
      setResetToken('');
      setImportRows([]);
      setImportWordFileName('');
      setImportFilterText('');
      setImportFilterStatus('all');
      setCurrentImportRowKey(null);
      setIsImportModalOpen(false);
      handleNew();
      refreshAyudas();
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || 'Error reiniciando tablas ISA.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Page>
      {/* ══ PANEL IZQUIERDO ══ */}
      <Left>
        <HeaderRow>
            <h3 style={{ margin: 0, fontSize: "14px" }}>Ayudas ISA</h3>
            <BtnGeneral bg="cyan" padding="5px 10px" radius="5px" fw="600" fs="11px" onClick={handleNew}>
                Nuevo
            </BtnGeneral>
        </HeaderRow>

        <SearchInput placeholder="Buscar por actividad, título, grupo..." value={search} onChange={(e) => setSearch(e.target.value)} />

        <RoleListContainer>
          {filteredAyudas.map((a, idx) => {
            // Find semantic label for activity
            const actMatch = consecutivos.find(c => String(c.Consecutivo) === String(a.actividad_consecutivo));
            const headerText = actMatch ? `${actMatch.Consecutivo} - ${actMatch.Api}` : `Actividad: ${a.actividad_consecutivo}`;
            
            return (
              <RoleItem
                key={`${a.actividad_consecutivo}_${a.grupo}_${a.subgrupo}_${idx}`}
                active={formData.id === a.id && formData.id !== null}
                onClick={() => handleSelectAyuda(a)}
              >
                <div>
                  <strong style={{ fontSize: "12px", color: '#0f172a' }}>{headerText}</strong>
                  <div style={{ fontSize: "10px", color: "#64748b", marginTop: "2px" }}>
                    {a.titulo || "Sin Título Configurado"}
                  </div>
                  {(a.grupo || a.subgrupo) && (
                    <div style={{ fontSize: "9px", color: "#94a3b8", marginTop: "1px" }}>
                      {a.grupo ? `Grupo: ${a.grupo}` : ''}{a.grupo && a.subgrupo ? ' • ' : ''}{a.subgrupo ? `Subgrupo: ${a.subgrupo}` : ''}
                    </div>
                  )}
                </div>
              </RoleItem>
            );
          })}
          {filteredAyudas.length === 0 && <span style={{ fontSize: '11px', color: '#94a3b8', padding: '10px' }}>No hay ayudas registradas.</span>}
        </RoleListContainer>
      </Left>

      {/* ══ PANEL DERECHO ══ */}
      <Right style={{ overflowY: "auto", padding: "10px 15px" }}>
        <input
          ref={wordInputRef}
          type="file"
          accept=".docx"
          style={{ display: 'none' }}
          onChange={handleWordFileChange}
        />
        <HeaderRow style={{ marginBottom: "15px" }}>
          <span style={{ fontSize: "13px", fontWeight: "600", color: "#334155" }}>
            {formData.id ? "Editor de Ayuda Existente" : "Crear Nueva Ayuda ISA"}
          </span>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <BtnGeneral
              bg="cyan"
              padding="6px 14px"
              radius="5px"
              fw="600"
              fs="12px"
              onClick={() => setIsImportModalOpen(true)}
              disabled={importingWord || loading}
            >
              {importRows.length > 0 ? `Ver importación (${importRows.length})` : 'Importar Word'}
            </BtnGeneral>
            <BtnGeneral bg="green" padding="6px 18px" radius="5px" fw="600" fs="12px" onClick={handleSave} disabled={loading}>
              {loading ? "Guardando..." : "Guardar"}
            </BtnGeneral>
          </div>
        </HeaderRow>

        <Modal
          isOpen={isImportModalOpen}
          onClose={() => setIsImportModalOpen(false)}
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap', paddingRight: '28px' }}>
              <span style={{ fontSize: '14px', fontWeight: 700 }}>
                Importación desde Word (.docx)
              </span>
              <span style={{ fontSize: '11px', color: '#cbd5e1', fontWeight: 400 }}>
                {importWordFileName ? `Archivo: ${importWordFileName}` : 'Sin archivo cargado'}
                {importRows.length > 0 ? ` | Total: ${importStats.total} | Guardadas: ${importStats.saved} | Pendientes: ${importStats.pending}` : ''}
              </span>
            </div>
          }
          width="min(1100px, 95vw)"
          maxH="88vh"
          padding="18px 20px"
          bodyOverflow="auto"
        >
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                <BtnGeneral
                  bg="blue"
                  padding="5px 12px"
                  radius="4px"
                  fw="600"
                  fs="11px"
                  onClick={handlePickWord}
                  disabled={importingWord || loading}
                >
                  {importingWord ? 'Cargando Word...' : 'Seleccionar Word'}
                </BtnGeneral>
              </div>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', minWidth: '320px', flex: 1, justifyContent: 'flex-end' }}>
                <input
                  style={inp}
                  placeholder="Buscar (Consecutivo, Grupo, Subgrupo o Título)..."
                  value={importFilterText}
                  onChange={(ev) => setImportFilterText(ev.target.value)}
                />
                <SearchableSelect
                  key={`import-status-${importFilterStatus}`}
                  value={importFilterStatus || "all"}
                  onValueChange={(val) => setImportFilterStatus(val)}
                  options={[
                    { value: "all", label: "Todas" },
                    { value: "pending", label: "Pendientes" },
                    { value: "saved", label: "Guardadas" },
                    { value: "duplicate", label: "Duplicadas en Word" },
                  ]}
                  multiple={false}
                  searchable={false}
                  size="small"
                  sx={{ width: "220px", flex: "unset", ...selectSx }}
                />
              </div>
            </div>

            {importRows.length > 0 && (
              <div style={{ marginTop: '12px', border: '1px solid #e2e8f0', borderRadius: '6px', overflow: 'hidden' }}>
                <div style={{ maxHeight: '420px', overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                    <thead style={{ position: 'sticky', top: 0, background: '#f1f5f9', zIndex: 1 }}>
                      <tr>
                        <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e2e8f0' }}>Estado</th>
                        <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e2e8f0' }}>Consecutivo</th>
                        <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e2e8f0' }}>Grupo</th>
                        <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e2e8f0' }}>Subgrupo</th>
                        <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e2e8f0' }}>Título</th>
                        <th style={{ width: '1%', padding: '6px 8px', borderBottom: '1px solid #e2e8f0' }} />
                      </tr>
                    </thead>
                    <tbody>
                      {filteredImportRows.map((r, idx) => {
                        const k = buildRowKey(r);
                        const isDup = k ? duplicateKeysInWord.has(k) : false;
                        const isSaved = k ? savedKeys.has(k) : false;
                        const statusText = isDup ? 'DUP' : (isSaved ? '✓' : '');
                        const rowBg = k && currentImportRowKey === k ? '#e0f2fe' : 'white';
                        return (
                          <tr key={`${k || idx}`} style={{ background: rowBg }}>
                            <td style={{ padding: '6px 8px', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap', color: isDup ? '#b91c1c' : '#065f46', fontWeight: 700 }}>
                              {statusText}
                            </td>
                            <td style={{ padding: '6px 8px', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>{r.actividad_consecutivo}</td>
                            <td style={{ padding: '6px 8px', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>{r.grupo}</td>
                            <td style={{ padding: '6px 8px', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>{r.subgrupo}</td>
                            <td style={{ padding: '6px 8px', borderBottom: '1px solid #e2e8f0', minWidth: '240px' }}>{r.titulo || ''}</td>
                            <td style={{ padding: '6px 8px', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>
                              <BtnGeneral
                                bg="cyan"
                                padding="4px 10px"
                                radius="4px"
                                fw="600"
                                fs="11px"
                                onClick={() => handleLoadImportRow(r)}
                                disabled={loading || isDup}
                              >
                                Cargar
                              </BtnGeneral>
                            </td>
                          </tr>
                        );
                      })}
                      {filteredImportRows.length === 0 && (
                        <tr>
                          <td colSpan={6} style={{ padding: '10px 8px', color: '#64748b' }}>
                            No hay resultados con los filtros actuales.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {Boolean(user?.es_supersu) && (
              <div style={{ marginTop: '16px', borderTop: '1px solid #e2e8f0', paddingTop: '12px' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: '#b91c1c', marginBottom: '8px' }}>
                  Administración (supersu)
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 12px' }}>
                  <Field lbl="Confirmación (escriba RESET_AYUDAS_ISA):" required>
                    <input
                      style={inp}
                      value={resetConfirmText}
                      onChange={(ev) => setResetConfirmText(ev.target.value)}
                    />
                  </Field>
                  <Field lbl="Token de reseteo:" required>
                    <input
                      style={inp}
                      value={resetToken}
                      onChange={(ev) => setResetToken(ev.target.value)}
                    />
                  </Field>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
                  <BtnGeneral
                    bg="red"
                    padding="6px 14px"
                    radius="5px"
                    fw="700"
                    fs="12px"
                    onClick={handleResetAyudasIsa}
                    disabled={loading}
                  >
                    Limpiar tablas ISA
                  </BtnGeneral>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #e2e8f0' }}>
              <BtnGeneral
                bg="gray"
                padding="6px 14px"
                radius="5px"
                fw="700"
                fs="12px"
                onClick={() => setIsImportModalOpen(false)}
              >
                Cerrar
              </BtnGeneral>
            </div>
          </div>
        </Modal>

        <Accordion title="Datos Básicos y Relaciones" defaultOpen={true}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 20px" }}>
            <Field lbl="Título de la Ayuda:" required>
              <input 
                 style={inp} 
                 placeholder="Ej. Prevención y Control" 
                 value={formData.titulo} 
                 onChange={(e) => handleChange('titulo', e.target.value)} 
              />
            </Field>

            <Field lbl="Fecha de Revisión Oficial:" required>
              <input 
                 type="date"
                 style={inp} 
                 value={formData.fecha_revision} 
                 onChange={(e) => handleChange('fecha_revision', e.target.value)} 
              />
            </Field>

            <Field lbl="Consecutivo Actividad:" required>
              <SearchableSelect
                key={`ayuda-cons-${formData.id || formData.actividad_consecutivo || "empty"}`}
                placeholder="Seleccione Consecutivo..."
                options={consecutivoOptions}
                value={formData.actividad_consecutivo ? String(formData.actividad_consecutivo) : ""}
                onValueChange={(val) => handleChange('actividad_consecutivo', val || '')}
                multiple={false}
                searchable={true}
                size="small"
                sx={selectSx}
              />
            </Field>

            <Field lbl="Grupo Categoria:" required>
              <SearchableSelect
                key={`ayuda-grupo-${formData.id || formData.grupo || "empty"}`}
                placeholder="Seleccione Grupo..."
                options={grupoOptions}
                value={formData.grupo ? String(formData.grupo) : ""}
                onValueChange={(val) =>
                  setFormData((prev) => ({
                    ...prev,
                    grupo: val || "",
                    subgrupo: "",
                  }))
                }
                multiple={false}
                searchable={true}
                size="small"
                sx={selectSx}
              />
            </Field>

            <Field lbl="Subgrupo Dependiente:" required>
              <SearchableSelect
                key={`ayuda-subgrupo-${formData.id || formData.subgrupo || "empty"}`}
                placeholder={formData.grupo ? "Seleccione Subgrupo..." : "Selecciona un grupo primero"}
                disabled={!formData.grupo}
                options={subgrupoOptions}
                value={formData.subgrupo ? String(formData.subgrupo) : ""}
                onValueChange={(val) => handleChange("subgrupo", val || "")}
                multiple={false}
                searchable={true}
                size="small"
                sx={selectSx}
              />
            </Field>
          </div>
        </Accordion>

        <Accordion title="Etiquetas / Keywords (Pills)" defaultOpen={false}>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <div style={{ flex: 1 }}>
              <input 
                  style={inp}
                  placeholder="Escribe y presiona Enter para agregar..."
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addTag()}
              />
            </div>
            <BtnGeneral bg="blue" padding="5px 12px" radius="4px" onClick={addTag}>Añadir</BtnGeneral>
          </div>
          <div style={{ display: 'flex', gap: '8px', marginTop: '15px', flexWrap: 'wrap' }}>
            {formData.tags.map((tag, i) => (
                <Chip key={i} label={tag} onDelete={() => removeTag(i)} color="primary" variant="outlined" size="small" />
            ))}
          </div>
        </Accordion>

        <Accordion title="Descripción y Condición" defaultOpen={true}>
          <EditorContainer>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold', color: '#475569', fontSize: '11px' }}>DESCRIPCIÓN GENERAL</Typography>
            <ReactQuill theme="snow" modules={modules} value={formData.desc_richtext} onChange={(val) => handleChange('desc_richtext', val)} />
          </EditorContainer>
          <EditorContainer>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold', color: '#475569', fontSize: '11px' }}>CONDICIÓN Y CRITERIO DE APLICACIÓN</Typography>
            <ReactQuill theme="snow" modules={modules} value={formData.cond_richtext} onChange={(val) => handleChange('cond_richtext', val)} />
          </EditorContainer>
        </Accordion>

        <Accordion title="Intervenciones y Detalles Operativos" defaultOpen={false}>
          <EditorContainer>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold', color: '#475569', fontSize: '11px' }}>PUNTOS CLAVE DE LA INTERVENCIÓN</Typography>
            <ReactQuill theme="snow" modules={modules} value={formData.puntos_richtext} onChange={(val) => handleChange('puntos_richtext', val)} />
          </EditorContainer>
          <EditorContainer>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold', color: '#065f46', fontSize: '11px' }}>INFORMACIÓN DE LA INTERVENCIÓN (Evaluación, alarma, Consejos)</Typography>
            <ReactQuill theme="snow" modules={modules} value={formData.info_interv_richtext} onChange={(val) => handleChange('info_interv_richtext', val)} />
          </EditorContainer>
        </Accordion>

        <Accordion title="Periodicidad y Excepciones" defaultOpen={false}>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '16px' }}>
            <EditorContainer>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold', color: '#475569', fontSize: '11px' }}>FRECUENCIA Y PERIODICIDAD</Typography>
              <ReactQuill theme="snow" modules={modules} value={formData.freq_richtext} onChange={(val) => handleChange('freq_richtext', val)} />
            </EditorContainer>
            <EditorContainer>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold', color: '#475569', fontSize: '11px' }}>EXCEPCIONES</Typography>
              <ReactQuill theme="snow" modules={modules} value={formData.excep_richtext} onChange={(val) => handleChange('excep_richtext', val)} />
            </EditorContainer>
          </div>
        </Accordion>

        <Accordion title="Fuente" defaultOpen={false}>
          <EditorContainer style={{ marginBottom: 0 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold', color: '#475569', fontSize: '11px' }}>FUENTE</Typography>
            <ReactQuill theme="snow" modules={modules} value={formData.source_rich} onChange={(val) => handleChange('source_rich', val)} />
          </EditorContainer>
        </Accordion>

      </Right>
    </Page>
  );
}
